import { Link, Navigate } from "react-router-dom";
import { LogOut, User, Briefcase, Mail, CheckCircle2, FileText, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABELS = {
  travel_partner: "Travel Partner",
  corporate_member: "Corporate Member",
  government_member: "Government Member",
  advisory_member: "Advisory Member",
  strategic_partner: "Strategic Partner",
  innovative_partner: "Innovative Partner",
  executive_board: "Executive Board",
  sponsor: "Sponsor",
  investor: "Investor",
  traveler: "Traveller",
};

const UserDashboardPage = () => {
  const { user, loading, logout } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="animate-spin w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) return <Navigate to="/signin" replace />;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="user-dashboard-page">
      {/* Header */}
      <header className="bg-[#0B132B] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="font-heading text-2xl font-bold" data-testid="dashboard-logo">
            <span className="text-white">WTT</span><span className="text-[#D4AF37]">Q</span><span className="text-white">.org</span>
            <span className="text-white/50 font-normal text-base ml-3">| Partner Portal</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-white/70 hover:text-white" data-testid="dashboard-view-site">View Site</Link>
            <button
              onClick={logout}
              className="flex items-center gap-2 text-sm text-white/70 hover:text-white"
              data-testid="dashboard-logout-button"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Welcome */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold mb-3">
            <CheckCircle2 className="w-3.5 h-3.5" /> Approved Member
          </div>
          <h1 className="font-heading text-4xl font-bold text-[#0B132B]">
            Welcome, {user.full_name.split(" ")[0]}
          </h1>
          <p className="text-[#4A5568] mt-2">Here's your WTTQ partner home.</p>
        </div>

        {/* Profile card */}
        <div className="bg-white border border-gray-100 p-8 mb-8" data-testid="profile-card">
          <h2 className="font-heading text-xl font-bold text-[#0B132B] mb-6">Your Profile</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <User className="w-5 h-5 text-[#D4AF37] mt-0.5" />
              <div>
                <p className="text-xs uppercase tracking-wider text-[#718096]">Name</p>
                <p className="text-[#0B132B] font-medium">{user.full_name}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-[#D4AF37] mt-0.5" />
              <div>
                <p className="text-xs uppercase tracking-wider text-[#718096]">Email</p>
                <p className="text-[#0B132B] font-medium break-all">{user.email}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Briefcase className="w-5 h-5 text-[#D4AF37] mt-0.5" />
              <div>
                <p className="text-xs uppercase tracking-wider text-[#718096]">Role</p>
                <p className="text-[#0B132B] font-medium">{ROLE_LABELS[user.role] || user.role}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link
            to="/#apply"
            className="group bg-white border border-gray-100 p-8 hover:border-[#D4AF37] transition-colors"
            data-testid="action-submit-application"
          >
            <div className="w-12 h-12 bg-[#FDF9EC] border border-[#E8D38E] rounded-full flex items-center justify-center mb-4">
              <FileText className="w-5 h-5 text-[#92710d]" />
            </div>
            <h3 className="font-heading text-lg font-bold text-[#0B132B] mb-2 group-hover:text-[#92710d] transition-colors">
              Submit a Certification Application
            </h3>
            <p className="text-sm text-[#4A5568] leading-relaxed">
              Apply to have your property or destination reviewed for a WTTQ quality mark.
            </p>
          </Link>

          <Link
            to="/#directory"
            className="group bg-white border border-gray-100 p-8 hover:border-[#D4AF37] transition-colors"
            data-testid="action-browse-directory"
          >
            <div className="w-12 h-12 bg-[#F1F5F9] border border-[#CBD5E1] rounded-full flex items-center justify-center mb-4">
              <Building2 className="w-5 h-5 text-[#64748b]" />
            </div>
            <h3 className="font-heading text-lg font-bold text-[#0B132B] mb-2 group-hover:text-[#64748b] transition-colors">
              Browse Certified Partners
            </h3>
            <p className="text-sm text-[#4A5568] leading-relaxed">
              Explore the full WTTQ directory of certified hotels and destinations.
            </p>
          </Link>
        </div>

        <div className="mt-10 text-sm text-[#718096]">
          More self-service features (managing your own listing, tracking applications) are coming soon.
        </div>
      </div>
    </div>
  );
};

export default UserDashboardPage;
